import { ApiError, path } from './client'
import type { ApiClient, Query } from './client'
import type { Page } from '@namu/protocol'
import type {
  Case,
  CaseConsent,
  CaseConsentPurpose,
  CaseConsentScope,
  CaseEvent,
  CasePriority,
  CaseStatus,
  Instant,
  Referral,
  ReferralStatus,
} from './types'

/**
 * A person's situation, followed over time.
 *
 * Modelled on what Primero actually does, because it is the most battle-tested
 * open-source protection case model available and it encodes lessons that are
 * expensive to relearn. Three of those lessons are visible in this file.
 *
 * There is no `update` for the body of a case. A case is changed by appending
 * an event, because the timeline is the record: a note, a status change, an
 * assignment, a contact. Primero derives status from which fields are filled
 * in, which is clever and means a status can change because somebody typed a
 * date. An explicit transition is duller and can be audited.
 *
 * A referral takes a `consentId` and the field is not optional. Consent is its
 * own row with a purpose, a grantor and an expiry, and a referral exists only
 * against one. A boolean would record the answer while losing the question, the
 * date, and who asked.
 *
 * And nothing here scores, ranks or triages a person. Priority is set by a
 * human. The failure being avoided is unaccountable decisions about people with
 * no oversight and no remedy, and the way not to build that is to not build it.
 *
 * ## Withdrawing is not deleting
 *
 * `consents.withdraw` posts to `.../withdraw` and does not send a `DELETE`,
 * which is what it used to do against a route that has never existed. The
 * server is right and the console moved: `case_consents` is append-only, and
 * the question a year later is not "may we share this today" but "what did this
 * person agree to, and when did they take it back". A deleted row answers
 * neither, and consent is the one thing in this system that may have to be
 * proved after the fact.
 *
 * ## Every response is unwrapped here
 *
 * The server answers a single record as `{ case }`, `{ event }`, `{ consent }`
 * or `{ referral }`, and a list as `{ items, nextCursor }`. This module is the
 * only place that knows that, so a screen holds a `Case` rather than something
 * wrapped. It was worth an `unwrap` helper rather than a cast at each call site
 * because the cast is what was there before: `get` was typed as returning a
 * `Case`, returned `{ case: … }`, and every field the screen read came back
 * undefined with nothing anywhere reporting a fault.
 */

/**
 * Reads one record out of the envelope the server sends it in.
 *
 * A missing envelope is raised as a failure rather than passed on as
 * `undefined`. Handing `undefined` to a screen is the worse of the two: the
 * caller that opens a newly created case would navigate to `/cases/undefined`,
 * and the caller that renders one would draw a heading with no text in it. Both
 * of those read as the console being broken with no clue as to where. An
 * `ApiError` lands in the `catch` every one of these call sites already has.
 *
 * `retry` rather than `fatal`, because the realistic cause is a proxy or a
 * half-deployed server rather than a request that can never work.
 */
function unwrap<T>(body: unknown, key: string): T {
  const record = (body as Record<string, unknown> | null)?.[key]
  if (record === undefined || record === null) throw unreadable()
  return record as T
}

/**
 * The same for a page, which has to survive one more thing.
 *
 * `items` being absent or not an array is treated as a failure and not as an
 * empty list, and that distinction is the whole reason this function exists. An
 * empty caseload and an unreadable answer look identical on screen, and the
 * screen says "No cases have been opened" for both: somebody reads that as a
 * fact about their organization and stops looking.
 */
function pageFrom<T>(body: unknown): Page<T> {
  const envelope = body as { items?: unknown; nextCursor?: unknown } | null
  if (!Array.isArray(envelope?.items)) throw unreadable()
  return {
    items: envelope.items as T[],
    ...(typeof envelope.nextCursor === 'string' ? { nextCursor: envelope.nextCursor } : {}),
  }
}

function unreadable(): ApiError {
  return new ApiError({
    status: 200,
    code: 'internal',
    message: 'Namu sent an answer this console could not read.',
    recovery: 'retry',
  })
}

/**
 * The most consent or referral rows one case can be shown.
 *
 * Both lists are cursor paged on the server and both are walked to the end
 * here, because the referral control is enabled or disabled on the basis of
 * what is in these lists and a first page is not the record. The ceiling is a
 * guard against a cursor that does not move rather than a real expectation: a
 * case carrying two hundred consent records does not exist, and if one ever
 * does, the server still checks the chosen consent when the referral is made.
 */
const WHOLE_RECORD = 200

/**
 * Walks a cursor to the end, validating each page on the way.
 *
 * Written here rather than using the client's own `collect`, which is a better
 * helper in every respect but one: it spreads `page.items` straight into an
 * array, so a body without an `items` array escapes as a bare `TypeError` from
 * inside the client. That reaches a screen as an unhandled exception rather
 * than as a failure the screen can describe, which is the one thing this module
 * exists to prevent. Fixing it in `client.ts` would be the better answer and
 * that file is shared with the twelve other endpoint modules.
 */
async function walk<T>(http: ApiClient, at: string): Promise<T[]> {
  const items: T[] = []
  let cursor: string | undefined

  for (;;) {
    const page = pageFrom<T>(
      await http.request(at, {
        query: { limit: WHOLE_RECORD, ...(cursor === undefined ? {} : { cursor }) },
      }),
    )
    items.push(...page.items)

    // A cursor that does not move is a bug in the encoder, and in a browser it
    // takes the shape of a loop that fetches the same page until the tab is
    // killed. Stopping with what is in hand is the honest failure.
    if (page.nextCursor === undefined || page.nextCursor === cursor) return items
    if (items.length >= WHOLE_RECORD) return items
    cursor = page.nextCursor
  }
}

export interface CaseListQuery extends Query {
  projectId?: string
  status?: CaseStatus
  priority?: CasePriority
  /** A staff user id, or `me`, which the server resolves from the principal. */
  assignedTo?: string
  /**
   * The short human id, matched exactly and case-insensitively.
   *
   * Not a free-text search, and the difference is worth stating because the
   * console used to send `q` and be answered with the unfiltered caseload. This
   * is the lookup that actually happens: two caseworkers say "KAN-014" to each
   * other and one of them types it.
   */
  reference?: string
  limit?: number
  cursor?: string
}

export interface CaseInput {
  projectId: string
  /**
   * Supplied rather than minted by the server, and that is a product decision
   * rather than an omission. References come off paper: an organization already
   * numbers its files, and a server that invents `CASE-1` alongside a register
   * that says `KAN-014` gives two caseworkers two different names for one case.
   * Unique within the organization; a clash comes back as `bad_state`.
   */
  reference: string
  category: string
  priority?: CasePriority
  /** Omitted when the case is about somebody who never used the app. */
  accountId?: string
  assignedTo?: string
}

export interface CaseNoteInput {
  body: string
  /** A contact is a note that also records that somebody was actually reached. */
  kind?: 'note' | 'contact'
}

export interface CaseConsentInput {
  purpose: CaseConsentPurpose
  grantor: 'self' | 'guardian'
  scope: CaseConsentScope
  /**
   * Whether they agreed. A refusal is recorded too, and this is why the field
   * is not optional: "they said no" and "nobody asked" are different facts
   * about a person, and only one of them means go and ask.
   */
  granted: boolean
  /** Who the guardian is. The server requires it when `grantor` is `guardian`. */
  grantorNote?: string
  expiresAt?: Instant
}

export interface ReferralInput {
  toOrganization: string
  service: string
  /** Not optional. See the module comment. */
  consentId: string
  toContact?: string
  note?: string
}

export function casesApi(http: ApiClient) {
  return {
    list(query: CaseListQuery = {}) {
      return http.list<Case>('/v1/org/cases', query)
    },

    async get(id: string) {
      return unwrap<Case>(await http.request(path`/v1/org/cases/${id}`), 'case')
    },

    async create(input: CaseInput) {
      const body = await http.request('/v1/org/cases', { method: 'POST', body: input })
      return unwrap<Case>(body, 'case')
    },

    /** The whole timeline, oldest first. It is the case, not a history of it. */
    async events(id: string, query: { limit?: number; cursor?: string } = {}) {
      return pageFrom<CaseEvent>(await http.request(path`/v1/org/cases/${id}/events`, { query }))
    },

    async addNote(id: string, input: CaseNoteInput) {
      const body = await http.request(path`/v1/org/cases/${id}/events`, {
        method: 'POST',
        body: { kind: input.kind ?? 'note', body: input.body },
      })
      return unwrap<CaseEvent>(body, 'event')
    },

    /**
     * Moves the case along the explicit lifecycle.
     *
     * `new → assessment → planned → in_progress → closed`, with `reopened`
     * coming back off `closed`. The server refuses a transition that is not on
     * that graph, and closing without a reason is one of the refusals.
     */
    async setStatus(id: string, status: CaseStatus, closeReason?: string) {
      const body = await http.request(path`/v1/org/cases/${id}/status`, {
        method: 'POST',
        // `to` rather than `status`: the field names where the case is going,
        // and the server rejects the request outright under any other name.
        body: { to: status, ...(closeReason === undefined ? {} : { closeReason }) },
      })
      return unwrap<Case>(body, 'case')
    },

    async assign(id: string, userId: string | null) {
      const body = await http.request(path`/v1/org/cases/${id}/assignment`, {
        method: 'POST',
        // Null unassigns, which is a real act and gets its own timeline entry.
        body: { assignedTo: userId },
      })
      return unwrap<Case>(body, 'case')
    },

    consents: {
      /**
       * Every consent on the case, withdrawn and expired ones included.
       *
       * Walked to the end rather than taking a page. A consent missing from
       * this list is a consent the referral control cannot offer, and the
       * person in front of the screen is then told to go and ask for something
       * that was granted last week.
       */
      list(caseId: string) {
        return walk<CaseConsent>(http, path`/v1/org/cases/${caseId}/consents`)
      },

      async record(caseId: string, input: CaseConsentInput) {
        const body = await http.request(path`/v1/org/cases/${caseId}/consents`, {
          method: 'POST',
          body: input,
        })
        return unwrap<CaseConsent>(body, 'consent')
      },

      /**
       * A withdrawal is a new state on the row, never a deletion of it.
       *
       * The row stays and gains a `withdrawnAt`. Any referral standing on it
       * stops being covered and no new one can be created against it, which the
       * server enforces and this console repeats so that the control is
       * disabled rather than the request refused.
       */
      async withdraw(caseId: string, consentId: string) {
        const body = await http.request(
          path`/v1/org/cases/${caseId}/consents/${consentId}/withdraw`,
          { method: 'POST' },
        )
        return unwrap<CaseConsent>(body, 'consent')
      },
    },

    referrals: {
      /** Walked to the end, for the same reason the consent list is. */
      list(caseId: string) {
        return walk<Referral>(http, path`/v1/org/cases/${caseId}/referrals`)
      },

      async create(caseId: string, input: ReferralInput) {
        const body = await http.request(path`/v1/org/cases/${caseId}/referrals`, {
          method: 'POST',
          body: input,
        })
        return unwrap<Referral>(body, 'referral')
      },

      async setStatus(caseId: string, referralId: string, status: ReferralStatus) {
        const body = await http.request(path`/v1/org/cases/${caseId}/referrals/${referralId}`, {
          method: 'PATCH',
          body: { status },
        })
        return unwrap<Referral>(body, 'referral')
      },
    },
  }
}

export type CasesApi = ReturnType<typeof casesApi>
